import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useRef } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { getDialogOnInteractOutsideHandler, getDialogOnOpenAutoFocusHandler } from "@/components/Dialog/utils.ts"
import { Horizontal, Spring } from "@/components/Flex.ts"
import { Heading4 } from "@/components/Heading/Heading.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CloseLight } from "@/icons/Icons.ts"
import { HeadingStyles } from "@/styles/Markdown.ts"
import { dropdownShadow, mobileBreakpoint } from "@/styles/mixins.ts"

import type { ButtonKind } from "@/components/Button/Button.tsx"
import type { Property } from "csstype"
import type { PropsWithChildren, ReactNode } from "react"

type DialogProps = PropsWithChildren & {
  open?: boolean
  onOpenChange?: (open: boolean) => void

  trigger: ReactNode

  title: ReactNode
  description?: ReactNode

  top?: Property.Top
  maxWidth?: Property.MaxWidth
}

export const Dialog = (
  {
    open,
    onOpenChange,
    trigger,
    title,
    description,
    top,
    maxWidth,
    children
  }: DialogProps
) => {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Trigger asChild>
        {trigger}
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogContent
          ref={contentRef}
          top={top}
          maxWidth={maxWidth}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          onEscapeKeyDown={(e) => e.stopPropagation()}
          onOpenAutoFocus={getDialogOnOpenAutoFocusHandler(contentRef)}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={getDialogOnInteractOutsideHandler()}
        >
          <DialogScrollable childrenContainerClassName={DialogScrollableInner}>
            <DialogTitle className={HeadingStyles.Heading2}>{title}</DialogTitle>
            {description && (
              <DialogDescription asChild>
                <Heading4>{description}</Heading4>
              </DialogDescription>
            )}
            <Tooltip content="Close">
              <DialogClose asChild>
                <Button kind="icon">
                  <CloseLight />
                </Button>
              </DialogClose>
            </Tooltip>
            {children}
          </DialogScrollable>
        </DialogContent>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

type ConfirmDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void

  disabled?: boolean

  trigger: ReactNode
  description: ReactNode

  confirmText: string
  confirmKind?: ButtonKind
  onConfirm: () => void
}

export const ConfirmDialog = (
  {
    open,
    onOpenChange,
    disabled,
    trigger,
    description,
    confirmText,
    confirmKind = "danger",
    onConfirm
  }: ConfirmDialogProps
) => (
  <Dialog
    open={open}
    onOpenChange={onOpenChange}
    trigger={trigger}
    title="Please confirm"
    description={description}
  >
    <Horizontal alignItems="center" gap="8px">
      <Spring />
      <DialogPrimitive.Close asChild>
        <Button kind="alternative" onClick={e => e.stopPropagation()}>Cancel</Button>
      </DialogPrimitive.Close>
      <DialogPrimitive.Close asChild>
        <Button kind={confirmKind} disabled={disabled} onClick={(e) => {
          e.stopPropagation()
          onConfirm()
        }} data-testid="Confirm">
          {confirmText}
        </Button>
      </DialogPrimitive.Close>
    </Horizontal>
  </Dialog>
)

export const DialogOverlay = styled(DialogPrimitive.Overlay)`
  position: fixed;
  inset: 0;

  background-color: var(--glass-background-color);

  z-index: var(--z-index-dropdown);

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  &[data-state="open"] {
    animation: fade-in 0.1s ease-out;
  }
`

export const DialogContent = styled(DialogPrimitive.Content)<{
  top?: Property.Top
  maxWidth?: Property.MaxWidth,
}>`
  position: fixed;
  transform: ${props => props.top === undefined ? "translate(-50%, -50%)" : "translateX(-50%)"};
  left: 50%;
  top: ${props => props.top === undefined ? "50%" : props.top};

  background-color: var(--island-background-color);
  clip-path: inset(0 round var(--border-radius-32));

  ${dropdownShadow};

  width: 90vw;
  max-width: ${props => props.maxWidth || "720px"};

  outline: none;

  z-index: var(--z-index-dropdown);

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  &[data-state="open"] {
    animation: fade-in 0.1s ease-out;
  }
`

const DialogScrollable = styled(Scrollable)`
  max-height: 90vh;
`

const DialogScrollableInner = css`
  padding: 24px;
`

const DialogTitle = styled(DialogPrimitive.Title)`
  ${HeadingStyles.Heading2};

  h2& {
    margin: -24px 0 0;
    padding: 16px 0;

    line-height: 24px;
  }

  &:empty {
    display: none;
  }
`

const DialogDescription = styled(DialogPrimitive.Description)`
  margin-bottom: 16px;
`

export const DialogClose = styled(DialogPrimitive.Close)`
  border-radius: var(--border-radius-round);
  z-index: var(--z-index-dropdown);

  position: absolute;
  right: 16px;
  top: 16px;

  @media ${mobileBreakpoint} {
    top: 13px;
  }
`
